import { useMemo } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import { ColumnDef } from '@tanstack/react-table';
import { Bot, Copy, Edit, Plus, Search, Trash } from 'lucide-react';
import { Link } from 'react-router-dom';
import useScenarios from '@/hooks/useScenarios';
import { IScenario } from '@/types';
import axiosClient from '@/utils/axiosClient';
import DataTable from '../DataTable/DataTable';
import Section from '../Section';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';

const Scenarios = () => {
  const scenarios = useScenarios();
  const drawer = useModal('scenario-drawer');
  const data = useMemo(() => scenarios.data || [], [scenarios.data]);

  const handleClone = async (scenario: any) => {
    try {
      const response = await axiosClient.post(`v1/scenarios/${scenario.id}/clone`);
      if (response.data) {
        scenarios.refetch();
      }
    } catch (error) {
      console.error('Error cloning agent:', error);
    }
  };

  const handleArchive = async (scenario: any) => {
    try {
      const response = await axiosClient.post(`v1/scenarios/${scenario.id}/archive`);
      if (response.data) {
        scenarios.refetch();
      }
    } catch (error) {
      console.error('Error archiving scenario:', error);
    }
  };

  const columns: ColumnDef<IScenario>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'scenario_type.name',
        header: 'Type',
      },
      {
        accessorKey: 'description',
        header: 'Description',
      },
      {
        header: 'Actions',
        cell: ({ row }) => {
          const scenario = row.original;

          return (
            <div className="flex items-center gap-2">
              <Button asChild>
                <Link to={`/scenarios/${scenario.id}`}>
                  <Search className="mr-1" />
                  View
                </Link>
              </Button>

              <Button
                onClick={() => {
                  drawer.show({ scenario });
                }}
              >
                <Edit className="mr-1" />
                Edit
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button>
                    <Copy className="mr-1" />
                    Clone
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Clone Scenario</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to clone the scenario "{scenario.name}"?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleClone(scenario)}>Clone</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash className="mr-1" />
                    Trash
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Archive Scenario</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to archive the scenario "{scenario.name}"? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleArchive(scenario)}>Archive</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          );
        },
      },
    ],
    [drawer, handleArchive, handleClone],
  );

  return (
    <>
      <div className="flex items-center justify-between gap-2 mb-8">
        <div className="flex items-center gap-2 mb-4 ">
          <Bot />
          <div className="text-lg font-bold">Scenarios</div>
        </div>
        <Button className="ml-auto" onClick={() => drawer.show()}>
          <Plus className="mr-1" />
          Create New Scenario
        </Button>
      </div>
      <Section>
        <DataTable data={data} columns={columns} model={scenarios} />
      </Section>
    </>
  );
};

export default Scenarios;
